import CreateAxiosInstance from './axiosConfig';

const APIClient = CreateAxiosInstance(import.meta.env.VITE_API_BASE_URL);

/**
 * Get a single project with all its packets
 * @param {string} projectId - The project ID
 * @returns {Promise<Object>} { project, packets, is_owner, can_edit }
 */
export async function getProject(projectId) {
  return await APIClient.get(`/project/${projectId}`, {
    defaultErrorMessage: 'There was an error getting your resource. Please try again later.',
  });
}

/**
 * Get a specific version snapshot of a project
 * @param {string} projectId - The project ID
 * @param {string} versionId - The version UUID
 * @returns {Promise<Object>} The version snapshot blob
 */
export async function getProjectVersion(projectId, versionId) {
  return await APIClient.get(`/project/${projectId}/versions/${versionId}`, {
    defaultErrorMessage: 'There was an error getting this version. Please try again later.',
  });
}

/**
 * Restore a project to a specific version (by integer version_number)
 * @param {string} projectId - The project ID
 * @param {number} versionNumber - The integer version number to restore
 * @returns {Promise<Object>} The restored project (same shape as GET /project/:id)
 */
export async function restoreProjectVersion(projectId, versionNumber) {
  return await APIClient.post(
    `/project/${projectId}/versions/${versionNumber}/restore`,
    {},
    {
      headers: { 'Content-Type': 'application/json' },
      defaultErrorMessage: 'There was an error undoing this change. Please try again later.',
    }
  );
}

/**
 * Get list of user's projects and legacy packets (paginated)
 * @param {number} page - Page number (1-indexed)
 * @param {number} perPage - Items per page
 * @param {string} search - Search query
 * @param {boolean} onlyFavorites - Filter to favorites only
 * @returns {Promise<Object>} { results, pagination }
 */
export async function getProjects(
  page = 1,
  perPage = 10,
  search = '',
  onlyFavorites = false,
  collectionIds = []
) {
  let url = `/project?page=${page}&per_page=${perPage}`;
  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }
  if (onlyFavorites) {
    url += '&favorites=true';
  }
  if (collectionIds.length > 0) {
    url += `&collection_ids=${collectionIds.join(',')}`;
  }
  return await APIClient.get(url, {
    defaultErrorMessage: 'There was an error getting your resources. Please try again later.',
  });
}

/**
 * Create a new empty project
 * @param {string} title - Optional project title
 * @returns {Promise<Object>} The created project
 */
export async function createProject(title = null) {
  return await APIClient.post(
    '/project',
    { title },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      defaultErrorMessage: 'There was an error creating your resource. Please try again later.',
    }
  );
}

/**
 * Update a project
 * @param {string} projectId - The project ID
 * @param {Object} fields - Fields to update (title, is_favorite, settings, tab_order)
 * @param {string} [fields.title] - New project title
 * @param {boolean} [fields.is_favorite] - New favorite status
 * @param {Object} [fields.settings] - New project settings
 * @param {string[]} [fields.tab_order] - New tab order (array of packet IDs)
 * @returns {Promise<Object>} The updated project
 */
export async function updateProject(projectId, fields) {
  return await APIClient.put(`/project/${projectId}`, fields, {
    headers: {
      'Content-Type': 'application/json',
    },
    defaultErrorMessage: 'There was an error updating your resource. Please try again later.',
  });
}

/**
 * Delete a project (soft delete)
 * @param {string} projectId - The project ID
 * @returns {Promise<Object>} Success message
 */
export async function deleteProject(projectId) {
  return await APIClient.delete(`/project/${projectId}`, {
    defaultErrorMessage: 'There was an error deleting your resource. Please try again later.',
  });
}

/**
 * Add a packet to a project
 * @param {string} projectId - The project ID
 * @param {string} packetId - The packet ID to add
 * @param {string} title - Optional title override (sets packet.title on backend)
 * @param {boolean} clone - Whether to clone the packet (default: false)
 * @returns {Promise<Object>} Success response
 */
export async function addPacketToProject(projectId, packetId, title = null, clone = false) {
  return await APIClient.post(
    `/project/${projectId}/packets`,
    {
      packet_id: packetId,
      display_title: title, // Backend uses this to set packet.title
      clone: clone,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      defaultErrorMessage: 'There was an error adding the resource. Please try again later.',
    }
  );
}

/**
 * Update a packet within a project (position, title)
 * @param {string} projectId - The project ID
 * @param {string} packetId - The packet ID
 * @param {Object} fields - Fields to update (position, title)
 * @returns {Promise<Object>} Success response
 */
export async function updatePacketInProject(projectId, packetId, fields) {
  return await APIClient.put(`/project/${projectId}/packets/${packetId}`, fields, {
    headers: {
      'Content-Type': 'application/json',
    },
    defaultErrorMessage: 'There was an error updating the resource. Please try again later.',
  });
}

/**
 * Remove a packet from a project
 * @param {string} projectId - The project ID
 * @param {string} packetId - The packet ID to remove
 * @returns {Promise<Object>} Success response
 */
export async function removePacketFromProject(projectId, packetId) {
  return await APIClient.delete(`/project/${projectId}/packets/${packetId}`, {
    defaultErrorMessage: 'There was an error removing the resource. Please try again later.',
  });
}

/**
 * Reorder tabs in a project
 * @deprecated Use updateProject(projectId, { tab_order: [...] }) instead
 * @param {string} projectId - The project ID
 * @param {string[]} tabOrder - Array of packet IDs in new order
 * @returns {Promise<Object>} Success response
 */
export async function reorderProjectTabs(projectId, tabOrder) {
  return await APIClient.put(
    `/project/${projectId}/reorder`,
    { tab_order: tabOrder },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      defaultErrorMessage: 'There was an error reordering tabs. Please try again later.',
    }
  );
}

/**
 * Clone an entire project with all its packets
 * @param {string} projectId - The project ID to clone
 * @param {string} title - Optional new title for the cloned project
 * @param {Object} packetOverrides - Optional map of source packet ID to edited fields
 *   (blocks/title/settings/input_flags_json) applied on top of that packet's clone,
 *   so an in-flight edit isn't lost when a non-owner is redirected to their copy
 * @returns {Promise<Object>} The cloned project
 */
export async function cloneProject(projectId, title = null, packetOverrides = null) {
  return await APIClient.post(
    `/project/${projectId}/clone`,
    { title, ...(packetOverrides ? { packet_overrides: packetOverrides } : {}) },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      defaultErrorMessage: 'There was an error cloning your resource. Please try again later.',
    }
  );
}

/**
 * Toggle favorite status for a project
 * @deprecated Use updateProject(projectId, { is_favorite: true/false }) instead
 * @param {string} projectId - The project ID
 * @param {boolean} isFavorite - The new favorite state
 * @returns {Promise<Object>} Success response
 */
export async function toggleProjectFavorite(projectId, isFavorite) {
  return await APIClient.post(
    `/project/${projectId}/favorite`,
    { is_favorite: isFavorite },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      defaultErrorMessage: 'There was an error updating favorite status. Please try again later.',
    }
  );
}

// Alias for ProjectListContext compatibility
export const setProjectFavorite = toggleProjectFavorite;

/**
 * Set Public/Private visibility for every resource in a project.
 *
 * Public means other teachers can discover the resource (the share page is search-indexable
 * and eligible for library listings). Private means unlisted, not inaccessible — the share
 * link keeps working either way.
 *
 * Its own endpoint rather than updateProject(): PUT /project/{id} writes a version-history
 * entry on every call, and flipping visibility isn't a content edit.
 *
 * @param {string} projectId - The project ID
 * @param {boolean} isPublic - The new visibility
 * @returns {Promise<Object>} `{ id, is_public }`
 */
export async function updateProjectVisibility(projectId, isPublic) {
  return await APIClient.post(
    `/project/${projectId}/visibility`,
    { is_public: isPublic },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      defaultErrorMessage: 'There was an error updating who can see this. Please try again later.',
    }
  );
}
